import { useRef, useEffect } from 'react';
import { TEMPLATE_SIZE } from '../../../shared/lib/video';

interface ReelNormalizerProps {
  videoFile: File;
  onProgress: (progress: number) => void;
  onComplete: (blob: Blob) => void;
  onError: (error: string) => void;
}

/**
 * Прогоняет видео через canvas + MediaRecorder, чтобы получить файл, который
 * гарантированно принимает Instagram Reels API (constant 30fps, baseline H.264,
 * no B-frames). Это та же техника, что и в VideoProcessor для режима "с шаблоном",
 * но без рисования логотипа, текста и intro-кадра.
 */
export function ReelNormalizer({ videoFile, onProgress, onComplete, onError }: ReelNormalizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (videoFile && !processingRef.current) {
      processingRef.current = true;
      processVideo();
    }
  }, [videoFile]);

  async function processVideo() {
    let videoUrl = '';
    let audioCtx: AudioContext | null = null;

    try {
      const video = document.createElement('video');
      videoUrl = URL.createObjectURL(videoFile);
      video.src = videoUrl;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Failed to load video'));
      });

      if (video.readyState < 3) {
        await new Promise<void>((resolve) => {
          video.oncanplay = () => resolve();
        });
      }

      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Canvas not found');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      canvas.width = TEMPLATE_SIZE.width;
      canvas.height = TEMPLATE_SIZE.height;

      const fps = 30;
      const duration = video.duration;

      audioCtx = new AudioContext();
      const audioSource = audioCtx.createMediaElementSource(video);
      const audioDest = audioCtx.createMediaStreamDestination();
      audioSource.connect(audioDest);

      const canvasStream = canvas.captureStream(fps);
      const audioTracks = audioDest.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        canvasStream.addTrack(audioTracks[0]);
      }

      const mimeType = chooseMimeType();
      console.log('ReelNormalizer mimeType:', mimeType);

      const recorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 4_000_000,
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.start(100);

      video.currentTime = 0;
      video.muted = false;
      try {
        await video.play();
      } catch {
        video.muted = true;
        await video.play();
      }

      await new Promise<void>((resolve) => {
        let lastProgress = 0;

        const draw = () => {
          if (video.ended) {
            resolve();
            return;
          }
          drawVideoFrame(video, canvas, ctx);
          const progress = Math.min((video.currentTime / duration) * 100, 100);
          if (progress - lastProgress > 0.5) {
            onProgress(progress);
            lastProgress = progress;
          }
          requestAnimationFrame(draw);
        };

        video.onended = () => {
          drawVideoFrame(video, canvas, ctx);
          onProgress(100);
          resolve();
        };

        requestAnimationFrame(draw);
      });

      recorder.stop();
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });

      const blob = new Blob(chunks, { type: mimeType });
      console.log(`ReelNormalizer done: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
      onComplete(blob);
      processingRef.current = false;
    } catch (error) {
      console.error('ReelNormalizer error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error');
      processingRef.current = false;
    } finally {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (audioCtx) audioCtx.close().catch(() => {});
    }
  }

  function drawVideoFrame(video: HTMLVideoElement, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    const videoAspect = video.videoWidth / video.videoHeight;
    const canvasAspect = canvas.width / canvas.height;

    let drawWidth: number, drawHeight: number, offsetX: number, offsetY: number;
    if (videoAspect > canvasAspect) {
      drawWidth = canvas.width;
      drawHeight = drawWidth / videoAspect;
      offsetX = 0;
      offsetY = (canvas.height - drawHeight) / 2;
    } else {
      drawHeight = canvas.height;
      drawWidth = drawHeight * videoAspect;
      offsetX = (canvas.width - drawWidth) / 2;
      offsetY = 0;
    }

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', left: '-9999px', top: '-9999px', pointerEvents: 'none' }}
    />
  );
}

function chooseMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return 'video/webm';
}
